import type { MiddlewareData, Placement } from "@floating-ui/vue";
import { ref, watch, type CSSProperties, type Ref } from "vue";

const getAnchorXOffsets = (anchorRect: DOMRect, tooltipRect: DOMRect) => {
  const anchorWidth = anchorRect.width;
  const tooltipWidth = tooltipRect.width;

  const anchorXOffset = Math.abs(anchorRect.left - tooltipRect.left);
  const anchorXStart = `${(anchorXOffset / tooltipWidth) * 100}%`;
  const anchorXEnd = `${((anchorXOffset + anchorWidth) / tooltipWidth) * 100}%`;

  return { anchorXStart, anchorXEnd };
};

const getAnchorYOffsets = (anchorRect: DOMRect, tooltipRect: DOMRect) => {
  const anchorHeight = anchorRect.height;
  const tooltipHeight = tooltipRect.height;

  const anchorYOffset = Math.abs(anchorRect.top - tooltipRect.top);
  const anchorYStart = `${(anchorYOffset / tooltipHeight) * 100}%`;
  const anchorYEnd = `${((anchorYOffset + anchorHeight) / tooltipHeight) * 100}%`;

  return { anchorYStart, anchorYEnd };
};

export const useHoverBridgePosition = ({
  anchor,
  tooltip,
  placement,
  visible,
  middlewareData,
  offset,
}: {
  anchor: Ref<HTMLElement | undefined>;
  tooltip: Ref<HTMLElement | undefined>;
  placement: Ref<Placement>;
  visible: Ref<boolean>;
  middlewareData: Ref<MiddlewareData>;
  offset: number;
}) => {
  const hoverBridgeStyles = ref<CSSProperties>({});

  watch(
    [visible, middlewareData],
    () => {
      hoverBridgeStyles.value = {};

      if (!visible.value || !anchor.value || !tooltip.value) {
        return;
      }

      const anchorRect = anchor.value.getBoundingClientRect();
      const tooltipRect = tooltip.value.getBoundingClientRect();

      if (placement.value.includes("top")) {
        const { anchorXStart, anchorXEnd } = getAnchorXOffsets(
          anchorRect,
          tooltipRect,
        );

        hoverBridgeStyles.value = {
          blockSize: `${offset}px`,
          insetInline: "0",
          insetBlockStart: "100%",
          clipPath: `polygon(
            0% 0%, 
            100% 0%, 
            ${anchorXEnd} 100%, 
            ${anchorXStart} 100%
          )`,
        };
        return;
      }

      if (placement.value.includes("bottom")) {
        const { anchorXStart, anchorXEnd } = getAnchorXOffsets(
          anchorRect,
          tooltipRect,
        );

        hoverBridgeStyles.value = {
          blockSize: `${offset}px`,
          insetInline: "0",
          insetBlockEnd: "100%",
          clipPath: `polygon(
            ${anchorXStart} 0%,
            ${anchorXEnd} 0%,
            100% 100%,
            0% 100%
          )`,
        };
        return;
      }

      if (placement.value.includes("left")) {
        const { anchorYStart, anchorYEnd } = getAnchorYOffsets(
          anchorRect,
          tooltipRect,
        );

        hoverBridgeStyles.value = {
          inlineSize: `${offset}px`,
          insetBlock: "0",
          insetInlineStart: "100%",
          clipPath: `polygon(
            0% 0%, 
            100% ${anchorYStart}, 
            100% ${anchorYEnd}, 
            0% 100%
          )`,
        };
        return;
      }

      if (placement.value.includes("right")) {
        const { anchorYStart, anchorYEnd } = getAnchorYOffsets(
          anchorRect,
          tooltipRect,
        );

        hoverBridgeStyles.value = {
          inlineSize: `${offset}px`,
          insetBlock: "0",
          insetInlineEnd: "100%",
          clipPath: `polygon(
            0% ${anchorYStart}, 
            100% 0%, 
            100% 100%, 
            0% ${anchorYEnd}
          )`,
        };
        return;
      }
    },
    { flush: "post" },
  );

  return { hoverBridgeStyles };
};
